// 로봇 여러 대 고르기 — 시리얼 검색 + 눌러서 넣고 빼기. 서명 문서의 '로봇 수정'(2026-09-23
// 오너 요청)에서 쓴다. 새 서명 생성 창의 로봇 고르기와 같은 모양·같은 검색(/robots?search=).

import { useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

export interface PickedRobot {
  id: string;
  serialShort: string | null;
  modelDisplay: string;
}

const input: CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  fontSize: 13,
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  boxSizing: 'border-box',
};

export function RobotMultiPicker({
  value,
  onChange,
}: {
  value: PickedRobot[];
  onChange: (next: PickedRobot[]) => void;
}) {
  const [search, setSearch] = useState('');
  const q = useQuery({
    queryKey: ['robots-picker', search],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        items: Array<{ id: string; serialShort: string | null; model: { displayName: string } }>;
      }>('/robots', { params: { search: search || undefined, pageSize: 30 } });
      return (data.items ?? []).map((r) => ({
        id: r.id,
        serialShort: r.serialShort,
        modelDisplay: r.model.displayName,
      })) as PickedRobot[];
    },
  });

  return (
    <div data-testid="robot-multi-picker">
      <input
        style={input}
        placeholder="시리얼 번호 검색…"
        aria-label="로봇 시리얼 검색"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div
        style={{
          maxHeight: 180,
          overflowY: 'auto',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          marginTop: 4,
        }}
      >
        {(q.data ?? []).map((r) => {
          const checked = value.some((x) => x.id === r.id);
          return (
            <div
              key={r.id}
              role="option"
              aria-selected={checked}
              data-testid={`robot-option-${r.id}`}
              onClick={() => onChange(checked ? value.filter((x) => x.id !== r.id) : [...value, r])}
              style={{
                padding: '7px 12px',
                cursor: 'pointer',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: checked ? 'var(--bg-info-subtle)' : 'transparent',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              <input type="checkbox" checked={checked} readOnly style={{ pointerEvents: 'none' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                {r.serialShort ?? '–'}
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>{r.modelDisplay}</span>
            </div>
          );
        })}
        {q.isLoading && (
          <p
            style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}
          >
            불러오는 중…
          </p>
        )}
        {!q.isLoading && (q.data ?? []).length === 0 && (
          <p
            style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}
          >
            찾는 로봇이 없습니다.
          </p>
        )}
      </div>
      <div
        style={{
          fontSize: 12,
          color: value.length ? 'var(--fg-brand)' : 'var(--text-secondary)',
          marginTop: 4,
        }}
      >
        {value.length > 0
          ? `${value.length}대 선택: ${value.map((r) => r.serialShort ?? r.id).join(', ')}. 첫 번째 로봇이 대표 로봇입니다.`
          : '선택한 로봇이 없습니다. 이대로 저장하면 로봇 없는 문서가 됩니다.'}
      </div>
    </div>
  );
}
